import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import ScopedCommonTemplateScopeEdit from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/ScopedCommonTemplateScopeEdit";
import type { ScopedCommonTemplatesScopeChangeTracker, ScopedCommonTemplatesScopeKey } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedCommonTemplatesScopeChangeTracker";
import { EnvironmentChip } from "~/components/Chips";
import type { PopoverPosition } from "~/components/PopoverWhenFocused/PopoverWhenFocused";
import PopoverWhenFocused from "~/components/PopoverWhenFocused/index";
import ChangedValueIndicator from "../ChangedValueIndicator";
interface ScopedCommonTemplateScopeCellProps {
    environments: EnvironmentResource[];
    scopeTracker: ScopedCommonTemplatesScopeChangeTracker;
    scopeKey: ScopedCommonTemplatesScopeKey;
    focused: boolean;
    onFocus: () => void;
    onBlur: () => void;
    hasEditPermissions: boolean;
    hasScopeChanged: boolean;
    initialEnvironmentScope: string[];
}
export default ({ environments, scopeTracker, scopeKey, focused, onFocus, onBlur, hasEditPermissions, hasScopeChanged, initialEnvironmentScope }: ScopedCommonTemplateScopeCellProps) => (<ScopedCommonTemplateScopeEditableCell environments={environments} scopeTracker={scopeTracker} scopeKey={scopeKey} focused={focused} onFocus={onFocus} onBlur={onBlur} hasEditPermissions={hasEditPermissions} hasScopeChanged={hasScopeChanged} initialEnvironmentScope={initialEnvironmentScope}/>);
interface ScopedCommonTemplateScopeEditableCellProps extends ScopedCommonTemplateScopeCellProps {
    hasEditPermissions: boolean;
}
function ScopedCommonTemplateScopeEditableCell({ environments, scopeTracker, scopeKey, focused, onFocus, onBlur, hasEditPermissions, hasScopeChanged, initialEnvironmentScope }: ScopedCommonTemplateScopeEditableCellProps) {
    const calculateFocusedPopoverPosition = (): PopoverPosition => {
        return {
            top: 0,
            left: 0,
            right: 0,
        };
    };
    return (<div onFocus={onFocus} onClick={onFocus} className={cellStyles.container}>
            <PopoverWhenFocused isFocused={focused} onClickOutside={onBlur} dynamicPosition={calculateFocusedPopoverPosition}>
                {focused ? (<ScopedCommonTemplateScopeEdit scopeKey={scopeKey} scopeChangeTracker={scopeTracker} initialEnvironmentScope={initialEnvironmentScope} environments={environments}/>) : (<div className={cellStyles.readonlyContainer}>
                        <ScopedCommonTemplateReadonlyCell environments={environments} scopeKey={scopeKey} scopeChangeTracker={scopeTracker} initialEnvironmentScope={initialEnvironmentScope} hasEditPermissions={hasEditPermissions} hasScopeChanged={hasScopeChanged}/>
                        <div className={cellStyles.changedValueIndicator}>
                            <ChangedValueIndicator show={hasScopeChanged}/>
                        </div>
                    </div>)}
            </PopoverWhenFocused>
        </div>);
}
interface ScopedCommonTemplateScopeReadonlyCellProps {
    environments: EnvironmentResource[];
    scopeKey: ScopedCommonTemplatesScopeKey;
    scopeChangeTracker: ScopedCommonTemplatesScopeChangeTracker;
    initialEnvironmentScope: string[];
    hasEditPermissions: boolean;
    hasScopeChanged: boolean;
}
const ScopedCommonTemplateReadonlyCell = (props: ScopedCommonTemplateScopeReadonlyCellProps) => {
    const { environments, scopeKey, scopeChangeTracker, initialEnvironmentScope } = props;
    const environmentMap = new Map<string, EnvironmentResource>();
    environments.forEach((e) => environmentMap.set(e.Id, e));
    const scope = scopeChangeTracker.has(scopeKey) ? scopeChangeTracker.get(scopeKey) : initialEnvironmentScope;
    const scopeLength = scope?.length ?? 0;
    const tooManyChips = scopeLength >= 3;
    if (!scope?.length) {
        return null;
    }
    return (<div tabIndex={0}>
            {tooManyChips ? (<EnvironmentChip environmentName={`${scopeLength} environments`} key="environmentsChipsList" description={scope.map((s) => environmentMap.get(s)?.Name).join(", ")}/>) : (<>
                    {scope.map((s) => {
                const environment = environmentMap.get(s);
                return environment ? <EnvironmentChip environmentName={environment.Name} key={environment.Id}/> : null;
            })}
                </>)}
        </div>);
};
const cellStyles = {
    container: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "4rem",
    }),
    changedValueIndicator: css({
        marginLeft: "auto",
    }),
    readonlyContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: space[8],
        width: "100%",
        height: "100%",
        padding: `${space[8]} 0`,
    }),
};
